<template>
  <div class="svc-list">
    <div v-if="copiedFromLabel && !bannerDismissed" class="svc-list__banner">
      Servicios copiados de {{ copiedFromLabel }} · Actualiza los montos con las facturas del mes
      <button class="svc-list__banner-close" @click="bannerDismissed = true"><span class="mdi mdi-close"></span></button>
    </div>

    <div class="svc-list__header">
      <span class="svc-list__title">Servicios del mes <span class="svc-list__count">({{ services.length }} servicios)</span></span>
      <div class="svc-list__header-actions">
        <button class="svc-list__icon-btn svc-list__icon-btn--labeled" title="Configurar esquemas de distribución" @click="onGearClick">
          <span class="mdi mdi-scale-balance"></span>
          <span class="svc-list__icon-btn-label"> Esquemas</span>
        </button>
        <div class="svc-list__sort-wrap">
          <button class="svc-list__icon-btn" @click="sortMenuOpen = !sortMenuOpen"><span class="mdi mdi-sort"></span></button>
          <div v-if="sortMenuOpen" class="svc-list__sort-menu">
            <button @click="sortBy = 'name'; sortMenuOpen = false">Nombre A-Z</button>
            <button @click="sortBy = 'amount'; sortMenuOpen = false">Monto: mayor a menor</button>
            <button @click="sortBy = 'status'; sortMenuOpen = false">Estatus: pendientes primero</button>
          </div>
        </div>
      </div>
    </div>

    <div class="svc-list__body">
      <div
          v-for="s in sortedServices"
          :key="s.id"
          class="svc-card"
          :class="{ 'svc-card--new': s.isNew, 'svc-card--expanded': expandedIds.has(s.id), 'svc-card--cross-highlight': props.highlightedServiceIds.includes(s.id) }"
          :style="{ borderLeftColor: cardBorderColor(s) }"
        >
        <div class="svc-card__collapsed" @click="!s.isNew && toggleExpand(s.id)">
          <span class="svc-card__category-icon" :style="{ background: categoryMeta(s).color + '1f', color: categoryMeta(s).color }">
            <span class="mdi" :class="categoryMeta(s).icon"></span>
          </span>

          <div class="svc-card__info">
            <div class="svc-card__name">{{ s.name || 'Servicio sin nombre' }}</div>
            <span class="svc-card__scheme-tag">{{ SCHEME_LABELS[s.distributionScheme] || 'Sin esquema' }}</span>
          </div>

          <div class="svc-card__amount-wrap">
            <div class="svc-card__amount"><span class="svc-card__amount-currency">$</span>{{ formatNumber(s.amount) }}</div>
            <span class="svc-card__status-pill" :class="s.distributed ? 'svc-card__status-pill--distributed' : 'svc-card__status-pill--pending'">
              {{ s.distributed ? 'Distribuido' : 'Pendiente' }}
            </span>
          </div>

          <span v-if="s.unusual" class="mdi mdi-alert-outline svc-card__unusual-icon" :title="s.unusualDetail"></span>

          <div class="svc-card__actions">
            <button class="svc-card__action-btn" @click.stop="toggleExpand(s.id)"><span class="mdi mdi-pencil-outline"></span></button>
            <span class="mdi mdi-chevron-down svc-card__chevron" :class="{ 'svc-card__chevron--open': expandedIds.has(s.id) }" @click.stop="toggleExpand(s.id)"></span>
          </div>
        </div>

        <div v-if="expandedIds.has(s.id) || s.isNew" class="svc-card__expanded">
          <div v-if="s.isNew && !s.category" class="svc-card__category-picker">
              <button v-for="(meta, key) in CATEGORY_META" :key="key" class="svc-card__category-option" @click="setNewCategory(s, key)">
                <span class="svc-card__category-option-icon" :style="{ background: meta.color + '1f', color: meta.color }"><span class="mdi" :class="meta.icon"></span></span>
                <span>{{ meta.label }}</span>
              </button>
              <button
                v-for="type in customTypes"
                :key="type.id"
                class="svc-card__category-option"
                @click="setNewCustomType(s, type)"
              >
                <span class="svc-card__category-option-icon" :style="{ background: CATEGORY_META.CUSTOM.color + '1f', color: CATEGORY_META.CUSTOM.color }"><span class="mdi" :class="CATEGORY_META.CUSTOM.icon"></span></span>
                <span>{{ type.name }}</span>
              </button>
            </div>

          <template v-else>
            <div class="svc-card__expanded-grid">
              <div class="svc-card__expanded-col">
                <label class="svc-card__field-label">Nombre del servicio</label>
                <input v-model="editForm[s.id].name" class="svc-card__input" placeholder="Ej: Energía eléctrica común" />

                <label class="svc-card__field-label">Esquema de distribución</label>
                <select v-model="editForm[s.id].distributionScheme" class="svc-card__input">
                  <option :value="null">Selecciona esquema</option>
                  <option value="PROPORTIONAL_M2">Proporcional por m²</option>
                  <option value="EQUAL_PARTS">Partes iguales</option>
                </select>

                <label class="svc-card__field-label">N° de factura</label>
                <input v-model="editForm[s.id].invoiceNumber" class="svc-card__input" placeholder="Ej: FAC-2026-0847" />

                <label class="svc-card__field-label">Fecha de factura</label>
                <input v-model="editForm[s.id].invoiceDate" type="date" class="svc-card__input" />

                <label class="svc-card__field-label">Proveedor</label>
                    <div class="svc-card__autocomplete">
                      <input
                        v-model="editForm[s.id].provider"
                        class="svc-card__input"
                        placeholder="Nombre del proveedor"
                        autocomplete="off"
                        @input="onProviderInput(s)"
                        @focus="onProviderInput(s)"
                        @blur="onProviderBlur(s)"
                      />
                      <div v-if="providerDropdownOpen[s.id]" class="svc-card__autocomplete-list">
                        <button
                          v-for="p in providerSuggestions[s.id]"
                          :key="p.id"
                          class="svc-card__autocomplete-item"
                          @mousedown.prevent="selectProviderSuggestion(s, p)"
                        >
                          <span class="svc-card__autocomplete-name">{{ p.name }}</span>
                          <span v-if="p.rfc" class="svc-card__autocomplete-rfc">{{ p.rfc }}</span>
                        </button>
                      </div>
                    </div>

                    <label class="svc-card__field-label">RFC del proveedor</label>
                    <input v-model="editForm[s.id].rfc" class="svc-card__input" placeholder="Se autocompleta al elegir un proveedor" />
                    </div>

              <div class="svc-card__expanded-col">
                <div class="svc-card__amount-row">
                  <div>
                    <label class="svc-card__field-label">Subtotal</label>
                    <input v-model.number="editForm[s.id].subtotal" type="number" class="svc-card__input svc-card__input--mono" @input="recalcTotal(s.id)" />
                  </div>
                  <span class="svc-card__plus">+</span>
                  <div>
                    <label class="svc-card__field-label">IVA (16%)</label>
                    <input
                      v-model.number="editForm[s.id].ivaAmount"
                      type="number"
                      class="svc-card__input svc-card__input--mono"
                      :disabled="editForm[s.id].autoCalculateIva"
                    />
                  </div>
                </div>
                <label class="svc-card__checkbox-row">
                  <input type="checkbox" v-model="editForm[s.id].autoCalculateIva" @change="recalcTotal(s.id)" /> Calcular IVA automáticamente
                </label>

                <div class="svc-card__total-row">
                  <span>Total</span>
                  <span class="svc-card__total-value">{{ formatCurrency(computedTotal(s.id)) }}</span>
                </div>

                <div v-if="s.amount !== null && Math.abs(computedTotal(s.id) - s.amount) > 0.5" class="svc-card__discrepancy">
                  El total no coincide con el monto registrado
                  <button @click="editForm[s.id].amount = computedTotal(s.id)">Actualizar</button>
                </div>

                <label class="svc-card__field-label">Notas</label>
                <textarea v-model="editForm[s.id].notes" class="svc-card__textarea" rows="2" placeholder="Situaciones especiales que afecten este servicio o su distribución..."></textarea>
              </div>
            </div>

            <div class="svc-card__row-actions">
              <button class="svc-card__delete-btn" @click="confirmDelete(s.id)">Eliminar servicio</button>
              <button class="svc-card__cancel-btn" @click="cancelEdit(s.id)">Cancelar</button>
              <button class="svc-card__save-btn" :disabled="!editForm[s.id].amount && computedTotal(s.id) === 0" @click="saveService(s.id)">Guardar cambios</button>
            </div>
          </template>
        </div>
      </div>

      <button class="svc-list__add-btn" @click="onAddService">＋ Agregar servicio</button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, reactive } from 'vue'
import { getServicesForPeriod, updateService, deleteService, registerBlankService } from '@/services/services'
import { getServiceTypes, searchProviders } from '@/services/parkConfig'


const props = defineProps({
  parkId: { type: String, required: true },
  period: { type: String, required: true },
  highlightedServiceIds: { type: Array, default: () => [] },
})

const emit = defineEmits(['changed'])

const CATEGORY_META = {
  ELECTRICITY: { icon: 'mdi-lightning-bolt', color: '#eab308', label: 'Energía eléctrica' },
  WATER: { icon: 'mdi-water', color: '#0ea5e9', label: 'Agua industrial' },
  SECURITY: { icon: 'mdi-shield-outline', color: '#1d4ed8', label: 'Seguridad perimetral' },
  ROAD_MAINTENANCE: { icon: 'mdi-road-variant', color: '#6b7280', label: 'Mantenimiento de vialidades' },
  WASTE_COLLECTION: { icon: 'mdi-recycle', color: '#16a34a', label: 'Recolección de residuos' },
  LIGHTING: { icon: 'mdi-lightbulb-outline', color: '#f59e0b', label: 'Alumbrado exterior' },
  GAS: { icon: 'mdi-fire', color: '#ea580c', label: 'Gas' },
  CUSTOM: { icon: 'mdi-dots-grid', color: '#94a3b8', label: 'Otro' },
}

const SCHEME_LABELS = { PROPORTIONAL_M2: 'Proporcional por m²', EQUAL_PARTS: 'Partes iguales' }

const services = ref([])
const copiedFromLabel = ref(null)
const bannerDismissed = ref(false)
const expandedIds = ref(new Set())
const sortMenuOpen = ref(false)
const sortBy = ref('status')
const editForm = reactive({})

const customTypes = ref([])
const providerSuggestions = reactive({})
const providerDropdownOpen = reactive({})
let providerSearchTimeout = null

async function load() {
  const result = await getServicesForPeriod(props.parkId, props.period)
  services.value = result.services
  copiedFromLabel.value = result.copiedFromLabel
}
async function loadCustomTypes() {
  const result = await getServiceTypes(props.parkId)
  customTypes.value = result.custom || []
}
onMounted(() => { load(); loadCustomTypes() })
watch(() => props.period, load)

function categoryMeta(s) {
  const base = CATEGORY_META[s.category] || CATEGORY_META.CUSTOM
  if (s.category === 'CUSTOM' && s.customCategoryName) {
    return { ...base, label: s.customCategoryName }
  }
  return base
}

function cardBorderColor(s) {
  if (s.statusColor === 'red') return '#e84545'
  if (s.statusColor === 'green') return '#10b981'
  return '#f0a500'
}

const sortedServices = computed(() => {
  const list = [...services.value]
  if (sortBy.value === 'name') return list.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
  if (sortBy.value === 'amount') return list.sort((a, b) => (b.amount || 0) - (a.amount || 0))
  return list.sort((a, b) => Number(a.distributed) - Number(b.distributed))
})

function startEditForm(s) {
  editForm[s.id] = {
    name: s.name, distributionScheme: s.distributionScheme, invoiceNumber: s.invoiceNumber,
    invoiceDate: s.invoiceDate ? s.invoiceDate.slice(0, 10) : '', provider: s.provider, rfc: s.rfc,
    subtotal: s.subtotal, ivaAmount: s.ivaAmount, autoCalculateIva: s.autoCalculateIva,
    amount: s.amount, notes: s.notes,
  }
}

function toggleExpand(id) {
  if (expandedIds.value.has(id)) {
    expandedIds.value.delete(id)
  } else {
    const s = services.value.find((x) => x.id === id)
    startEditForm(s)
    expandedIds.value = new Set(expandedIds.value).add(id)
  }
}

function computedTotal(id) {
  const f = editForm[id]
  if (!f) return 0
  const subtotal = f.subtotal || 0
  const iva = f.autoCalculateIva ? subtotal * 0.16 : (f.ivaAmount || 0)
  return subtotal + iva
}

function recalcTotal(id) {
  const f = editForm[id]
  if (f.autoCalculateIva) f.ivaAmount = Math.round((f.subtotal || 0) * 0.16 * 100) / 100
}

async function saveService(id) {
  const f = editForm[id]
  const payload = { ...f, amount: f.amount || computedTotal(id) }
  await updateService(id, payload)
  expandedIds.value.delete(id)
  await load()
  emit('changed')
}

async function cancelEdit(id) {
  const s = services.value.find((x) => x.id === id)
  if (s.isNew) {
    await deleteService(id)
    services.value = services.value.filter((x) => x.id !== id)
  }
  expandedIds.value.delete(id)
}

async function confirmDelete(id) {
  if (!confirm('¿Eliminar este servicio?')) return
  await deleteService(id)
  expandedIds.value.delete(id)
  await load()
  emit('changed')
}

async function onAddService() {
  const result = await getServicesForPeriod(props.parkId, props.period)
  const created = await registerBlankService(result.periodId)
  const newRow = {
    id: created.id, name: null, category: null, distributionScheme: null, amount: null,
    subtotal: null, ivaAmount: null, autoCalculateIva: true, invoiceNumber: null, invoiceDate: null,
    provider: null, rfc: null, notes: null, distributed: false, statusColor: 'amber', unusual: false, isNew: true,  }
  services.value.push(newRow)
  startEditForm(newRow)
}

function setNewCategory(s, key) {
  s.category = key
  editForm[s.id].category = key
  s.customCategoryName = null
  editForm[s.id].customCategoryName = null
}

function setNewCustomType(s, type) {
  s.category = 'CUSTOM'
  editForm[s.id].category = 'CUSTOM'
  s.customCategoryName = type.name
  editForm[s.id].customCategoryName = type.name
}

function onGearClick() {
  emit('open-schemes')
}

function onProviderInput(s) {
  const query = editForm[s.id].provider
  clearTimeout(providerSearchTimeout)
  if (!query || query.trim().length < 2) {
    providerSuggestions[s.id] = []
    providerDropdownOpen[s.id] = false
    return
  }
  providerSearchTimeout = setTimeout(async () => {
    const category = editForm[s.id].category || s.category
    providerSuggestions[s.id] = await searchProviders(props.parkId, query, category)
    providerDropdownOpen[s.id] = providerSuggestions[s.id].length > 0
  }, 300)
}

function selectProviderSuggestion(s, provider) {
  editForm[s.id].provider = provider.name
  editForm[s.id].rfc = provider.rfc || editForm[s.id].rfc
  providerDropdownOpen[s.id] = false
}

function onProviderBlur(s) {
  setTimeout(() => { providerDropdownOpen[s.id] = false }, 150)
}

function formatNumber(n) {
  if (n === null || n === undefined) return '0'
  return new Intl.NumberFormat('es-MX', { maximumFractionDigits: 0 }).format(n)
}

function formatCurrency(n) {
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(n || 0)
}

defineExpose({ reload: load, addService: onAddService })
</script>

<style scoped>
.svc-list {
  flex: 0 0 55%;
  max-width: 55%;
  display: flex;
  flex-direction: column;
  background: var(--bg-surface-1);
  border: 1px solid var(--border-active);
  border-radius: 10px;
  overflow: hidden;
  margin: 0 0 1.5rem 1.5rem;
  min-height: 0;
}

.svc-list__banner {
  height: 32px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0 1rem;
  background: #eff6ff;
  font-family: var(--font-body);
  font-size: 11px;
  color: #3b82f6;
  flex-shrink: 0;
}

.svc-list__banner-close { margin-left: auto; background: none; border: none; color: #3b82f6; cursor: pointer; display: flex; }

.svc-list__header {
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 0.9rem;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
}

.svc-list__title { font-family: var(--font-body); font-size: 11px; font-weight: 500; text-transform: uppercase; letter-spacing: 0.8px; color: #94a3b8; }
.svc-list__count { text-transform: none; letter-spacing: 0; font-weight: 400; }

.svc-list__header-actions { display: flex; align-items: center; gap: 0.4rem; }
.svc-list__icon-btn { background: none; border: none; color: #94a3b8; cursor: pointer; display: flex; font-size: 14px; }
.svc-list__icon-btn:hover { color: #1e3a5f; }

.svc-list__sort-wrap { position: relative; }
.svc-list__sort-menu {
  position: absolute; top: calc(100% + 4px); right: 0; background: #ffffff; border: 1px solid #e2e8f0;
  border-radius: 8px; box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12); padding: 0.3rem; z-index: 100; min-width: 200px;
}
.svc-list__sort-menu button {
  display: block; width: 100%; text-align: left; background: none; border: none; font-family: var(--font-body);
  font-size: 12px; color: var(--text-content); padding: 0.4rem 0.5rem; cursor: pointer; border-radius: 5px;
}
.svc-list__sort-menu button:hover { background: #f8fafc; }

.svc-list__body { flex: 1; min-height: 0; overflow-y: auto; padding: 0.75rem; display: flex; flex-direction: column; gap: 0.5rem; }
.svc-list__body::-webkit-scrollbar { width: 6px; }
.svc-list__body::-webkit-scrollbar-thumb { background: var(--border-active); border-radius: 3px; }
.svc-list__body::-webkit-scrollbar-track { background: transparent; }

.svc-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-left: 3px solid;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
}

.svc-card--new { background: #eff6ff; border-color: #bfdbfe; }

.svc-card__collapsed {
  height: 56px;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0 14px;
  cursor: pointer;
}

.svc-card__category-icon {
  width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
  font-size: 16px; flex-shrink: 0;
}

.svc-card__info { flex: 1; min-width: 0; }
.svc-card__name { font-family: var(--font-body); font-size: 13px; font-weight: 500; color: #1e3a5f; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.svc-card__scheme-tag { display: inline-block; font-size: 10px; color: #64748b; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 4px; padding: 0.05rem 0.3rem; margin-top: 0.15rem; }

.svc-card__amount-wrap { display: flex; flex-direction: column; align-items: flex-end; gap: 0.15rem; flex-shrink: 0; }
.svc-card__amount { font-family: var(--font-mono); font-size: 14px; color: #1e3a5f; }
.svc-card__amount-currency { font-family: var(--font-body); font-size: 11px; color: #94a3b8; }

.svc-card__status-pill { height: 18px; display: flex; align-items: center; padding: 0 0.4rem; border-radius: 20px; font-family: var(--font-body); font-size: 10px; font-weight: 500; }
.svc-card__status-pill--distributed { background: #dcfce7; color: #16a34a; }
.svc-card__status-pill--pending { background: #fef3c7; color: #d97706; }

.svc-card__unusual-icon { color: #e84545; font-size: 14px; flex-shrink: 0; }

.svc-card__actions { display: flex; flex-direction: column; align-items: center; gap: 0.3rem; flex-shrink: 0; }
.svc-card__action-btn { background: none; border: none; color: #94a3b8; cursor: pointer; display: flex; font-size: 14px; }
.svc-card__action-btn:hover { color: #3b82f6; }
.svc-card__chevron { font-size: 14px; color: #94a3b8; cursor: pointer; transition: transform 150ms ease; }
.svc-card__chevron--open { transform: rotate(180deg); }

.svc-card__expanded { background: #fafafa; border-top: 1px solid #f1f5f9; padding: 0.75rem; }

.svc-card__category-picker { display: grid; grid-template-columns: repeat(4, 1fr); gap: 0.5rem; }
.svc-card__category-option { display: flex; flex-direction: column; align-items: center; gap: 0.3rem; background: none; border: none; cursor: pointer; font-family: var(--font-body); font-size: 10px; color: #64748b; }
.svc-card__category-option-icon { width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 18px; }

.svc-card__expanded-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
.svc-card__expanded-col { display: flex; flex-direction: column; gap: 0.35rem; }

.svc-card__field-label { font-family: var(--font-body); font-size: 10px; text-transform: uppercase; color: #94a3b8; margin-top: 0.3rem; }
.svc-card__input, .svc-card__textarea {
  width: 100%; box-sizing: border-box; font-family: var(--font-body); font-size: 13px; color: #1e3a5f;
  background: #ffffff; border: 1px solid #e2e8f0; border-radius: 4px; height: 32px; padding: 0 0.5rem;
}
.svc-card__input--mono { font-family: var(--font-mono); }
.svc-card__textarea { height: auto; padding: 0.4rem 0.5rem; resize: vertical; }

.svc-card__amount-row { display: flex; align-items: flex-end; gap: 0.4rem; }
.svc-card__plus { padding-bottom: 8px; color: #94a3b8; }
.svc-card__checkbox-row { display: flex; align-items: center; gap: 0.3rem; font-family: var(--font-body); font-size: 11px; color: #64748b; margin-top: 0.3rem; }

.svc-card__total-row { display: flex; justify-content: space-between; align-items: center; margin-top: 0.5rem; font-family: var(--font-body); font-size: 12px; color: #1e3a5f; }
.svc-card__total-value { font-family: var(--font-mono); font-size: 14px; font-weight: 700; }

.svc-card__discrepancy { display: flex; align-items: center; gap: 0.5rem; font-family: var(--font-body); font-size: 11px; color: #dc2626; margin-top: 0.4rem; }
.svc-card__discrepancy button { background: none; border: none; color: #dc2626; text-decoration: underline; cursor: pointer; font-size: 11px; }

.svc-card__row-actions { display: flex; align-items: center; margin-top: 0.75rem; }
.svc-card__delete-btn { background: none; border: none; color: #dc2626; font-family: var(--font-body); font-size: 12px; cursor: pointer; }
.svc-card__cancel-btn { background: none; border: none; color: #64748b; font-family: var(--font-body); font-size: 12px; cursor: pointer; margin: 0 auto; }
.svc-card__save-btn { background: #3b82f6; color: #ffffff; border: none; border-radius: 4px; padding: 0.4rem 0.8rem; font-family: var(--font-body); font-size: 12px; font-weight: 500; cursor: pointer; }
.svc-card__save-btn:disabled { opacity: 0.5; cursor: default; }

.svc-list__add-btn {
  height: 48px; background: transparent; border: 1px dashed #cbd5e1; border-radius: 8px;
  font-family: var(--font-body); font-size: 13px; color: #94a3b8; cursor: pointer; transition: all 100ms ease;
}
.svc-list__add-btn:hover { border-color: #3b82f6; color: #3b82f6; }
.svc-card--cross-highlight {
  background: #eff6ff;
  transition: background 80ms ease;
}
.svc-list__icon-btn--labeled {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-family: var(--font-body);
  font-size: 11px;
}

.svc-list__icon-btn-label {
  white-space: nowrap;
}

.svc-card__autocomplete { position: relative; }
.svc-card__autocomplete-list {
  position: absolute;
  top: calc(100% + 2px);
  left: 0;
  right: 0;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 50;
  max-height: 160px;
  overflow-y: auto;
}
.svc-card__autocomplete-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0.4rem 0.6rem;
  background: none;
  border: none;
  cursor: pointer;
  font-family: var(--font-body);
  font-size: 12px;
  color: #1e3a5f;
  text-align: left;
}
.svc-card__autocomplete-item:hover { background: #f8f9fc; }
.svc-card__autocomplete-name { font-weight: 500; }
.svc-card__autocomplete-rfc { font-size: 10px; color: #94a3b8; font-family: var(--font-mono); }
</style>